import { useCallback } from 'react';
import useWidgetCache from './useWidgetCache';
import { lookupComponent, normalizeComponentId } from '@utils';
/**
 * @export
 * Public API hook for accessing widgets and UIs throughout the application
 *
 * Use this hook when you need to:
 * - Get list of all widgets or UIs
 * - Look up a widget/UI by ID (e.g., for configuration forms)
 * - Reload widget/UI data
 *
 * For widget drawer specific features (filtering, pagination, etc.) use useDrawerItems instead
 *
 * @example
 * ```typescript
 * const { widgets, findWidgetById, reload, isLoading } = useWidgets()
 *
 * // Get all widgets
 * const allWidgets = widgets
 *
 * // Find specific widget
 * const myWidget = findWidgetById('my-widget-id')
 *
 * // Reload data
 * await reload()
 * ```
 */
export function useWidgets(options = {}) {
    // Use cache hook for data
    const { allWidgets, allUIs, isLoading, reload } = useWidgetCache(options);
    /**
     * Find a widget by ID
     */
    const findWidgetById = useCallback((id) => {
        const nid = normalizeComponentId(id);
        return allWidgets.find(w => normalizeComponentId(w.id) === nid);
    }, [allWidgets]);
    /**
     * Find a UI by ID
     */
    const findUIById = useCallback((id) => {
        const nid = normalizeComponentId(id);
        return allUIs.find(u => normalizeComponentId(u.id) === nid);
    }, [allUIs]);
    const findDetailsById = useCallback((id) => {
        var _a;
        const comp = lookupComponent(id);
        if (!comp)
            return { type: 'widget', details: undefined };
        const type = ((_a = comp.modes) === null || _a === void 0 ? void 0 : _a.includes('ui')) ? 'ui' : 'widget';
        return { type, details: comp };
    }, []);
    return {
        widgets: allWidgets,
        uis: allUIs,
        findWidgetById,
        findUIById,
        findDetailsById,
        reload,
        isLoading
    };
}
export default useWidgets;
